import { FC } from 'react';
import { IoAddOutline, IoCubeOutline } from 'react-icons/io5';

import { Anchor, Group, Loader } from '@mantine/core';

import { CompanyProfile, ComponentQuery } from 'models';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { RouterHelpers } from 'helpers/RouterHelpers';

import { SparseSectionPanel, useIsSparseSection } from 'components/company-profile/components/SparseSectionPanel';
import { ProfileSectionHelpers } from 'components/company-profile/helpers/ProfileSectionHelpers';
import { useProductsByCompany } from 'components/company-profile/hooks/use-products-by-company';
import { ProductTeaser } from 'components/product-teaser/ProductTeaser';
import { CarouselSection } from 'components/section/CarouselSection';
import { GridSection } from 'components/section/GridSection';
import { Section, SectionProps } from 'components/section/Section';
import { useDistributorPricesByComponentIds } from 'hooks/use-distributor-prices-by-component-ids';
import { useProductSearchCount } from 'hooks/use-product-search-count';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';

const Products: FC<{ company: CompanyProfile; isCarousel?: boolean } & SectionProps> = ({
    company,
    isCarousel,
    ...props
}) => {
    const { products, isLoading } = useProductsByCompany(company);
    const { priceFor } = useDistributorPricesByComponentIds(products.map((product) => product.id));

    const manufacturedQuery: Partial<ComponentQuery> = { manufacturer: company.id };
    const distributedQuery: Partial<ComponentQuery> = { distributor: company.id };

    const { count: manufacturedCount } = useProductSearchCount(manufacturedQuery);
    const { count: distributedCount } = useProductSearchCount(distributedQuery);

    const { workspace: inWorkspace, homepagePath } = useWorkspaceConfig();
    const isSparse = useIsSparseSection(products.length);

    if (!products.length && !isLoading) {
        return null;
    }

    const sparsePanel = isSparse && (
        <SparseSectionPanel
            key="sparse-panel"
            owner={{
                icon: <IoAddOutline />,
                title: 'Add your next product',
                description: 'Every product you add shows up here and in the catalog.',
                cta: 'Add product',
                href: RouterHelpers.urls.products.create({ company }),
            }}
            visitor={{
                icon: <IoCubeOutline />,
                title: 'Looking for more?',
                description: 'Browse the full product catalog to compare options.',
                cta: 'Browse catalog',
                href: (inWorkspace && homepagePath) || RouterHelpers.urls.products.overview(),
            }}
        />
    );

    const Layout = isCarousel ? CarouselSection : GridSection;

    return (
        <Section title="Products" id="products" {...props}>
            {products.length ? (
                <Layout cols={ProfileSectionHelpers.cols}>
                    {products.filter(Boolean).map((component) => (
                        <ProductTeaser
                            key={component.id}
                            product={component}
                            distributorPrice={priceFor(component.id)}
                        />
                    ))}
                    {sparsePanel}
                </Layout>
            ) : isLoading ? (
                <Loader size="xs" color="gray.5" />
            ) : null}

            {/* Only worth linking out when the search holds more than the cards above */}
            {manufacturedCount + distributedCount > products.length && (
                <Group gap="xl">
                    {manufacturedCount > 0 && (
                        <Anchor
                            size="md"
                            fw={600}
                            c="black"
                            target="_blank"
                            href={RouterHelpers.urls.products.search({ productsQuery: manufacturedQuery })}
                        >
                            View {manufacturedCount > 1 && 'all '}
                            {IntlHelpers.formatCount(manufacturedCount, 'product', 'products')}
                        </Anchor>
                    )}
                    {distributedCount > 0 && (
                        <Anchor
                            size="md"
                            fw={600}
                            c="black"
                            target="_blank"
                            href={RouterHelpers.urls.products.search({ productsQuery: distributedQuery })}
                        >
                            View {distributedCount > 1 && 'all '}
                            {manufacturedCount > 0
                                ? IntlHelpers.formatCount(
                                      distributedCount,
                                      'distributed product',
                                      'distributed products',
                                  )
                                : IntlHelpers.formatCount(distributedCount, 'product', 'products')}
                        </Anchor>
                    )}
                </Group>
            )}
        </Section>
    );
};

export { Products };
